import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AiChatApi } from '@constructor/ai-chat'
import { useFederation } from '@/features/microFrontend/useFederation'

type Props = {
  knowledgeModelId?: string
  initialExpanded?: boolean
}

export const useKnowledgeModelChat = ({ knowledgeModelId, initialExpanded }: Props) => {
  const { root: federationRoot } = useFederation()
  const chatApi = useRef<AiChatApi>()
  const { i18n } = useTranslation()

  const getAiChatApi = useCallback(() => {
    return federationRoot?.invokeRootHandler<[], AiChatApi>({ handlerName: 'getAiChatApi' })
  }, [federationRoot])

  const initChat = useCallback(async () => {
    if (chatApi.current || !federationRoot?.hasVersion('knowledge-center')) {
      return
    }

    if (!knowledgeModelId) {
      return
    }

    try {
      chatApi.current = await getAiChatApi()
      chatApi.current?.setConfig({
        knowledgeModelId,
        chatSessionParams: { language: i18n.language },
      })

      if (initialExpanded) {
        chatApi.current?.open()
      }
    } catch (error) {
      console.error('error', error)
    }
  }, [federationRoot, getAiChatApi, i18n.language, initialExpanded, knowledgeModelId])

  useEffect(() => {
    if (!chatApi.current) {
      return
    }

    chatApi.current.setConfig({
      knowledgeModelId,
      chatSessionParams: { language: i18n.language },
    })

    if (initialExpanded) {
      chatApi.current.open()
    }
  }, [i18n.language, initialExpanded, knowledgeModelId])

  return { initChat }
}
